import { useEffect, useState } from 'react';
import { hrApi } from '../../api/hr.js';

const STEPS = ['Configure', 'Preview', 'Confirm', 'Payslips'];

export default function Payroll() {
  const [runs, setRuns] = useState([]);
  const [step, setStep] = useState(0);
  const [run, setRun] = useState(null);
  const [preview, setPreview] = useState(null);
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  function reset() {
    setStep(0); setRun(null); setPreview(null); setItems([]); setError(null);
  }

  useEffect(() => {
    refreshRuns();
  }, []);
  function refreshRuns() {
    hrApi.payrollRuns().then((d) => setRuns(d?.items || []));
  }

  async function startRun(e) {
    e.preventDefault();
    setError(null);
    const fd = new FormData(e.currentTarget);
    try {
      const month = Number(fd.get('month'));
      const year = Number(fd.get('year'));
      const created = await hrApi.createPayrollRun(month, year, fd.get('notes') || null);
      setRun(created.run);
      setLoading(true);
      const prev = await hrApi.payrollPreview(created.run.id);
      setPreview(prev);
      setStep(1);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed');
    } finally {
      setLoading(false);
    }
  }

  async function loadExisting(r) {
    setError(null);
    setRun(r);
    setLoading(true);
    try {
      if (r.status === 'completed') {
        const its = await hrApi.payrollItems(r.id);
        setItems(its?.items || []);
        setStep(3);
      } else {
        const prev = await hrApi.payrollPreview(r.id);
        setPreview(prev);
        setStep(1);
      }
    } catch (err) {
      setError(err.response?.data?.message);
    } finally {
      setLoading(false);
    }
  }

  async function confirmRun() {
    setLoading(true);
    setError(null);
    try {
      await hrApi.processPayroll(run.id);
      const its = await hrApi.payrollItems(run.id);
      setItems(its?.items || []);
      refreshRuns();
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.message || 'Process failed');
    } finally {
      setLoading(false);
    }
  }

  async function generate(itemId) {
    try {
      await hrApi.generatePayslip(itemId);
      const its = await hrApi.payrollItems(run.id);
      setItems(its?.items || []);
    } catch (err) {
      setError(err.response?.data?.message);
    }
  }

  return (
    <section>
      <header className="page-header">
        <h1>Payroll</h1>
        <button className="btn ghost" onClick={reset}>Start over</button>
      </header>

      <div className="wizard">
        {STEPS.map((s, i) => (
          <div key={s} className={`wstep ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`}>
            <span className="wnum">{i + 1}</span> {s}
          </div>
        ))}
      </div>

      {error && <div className="alert error">{error}</div>}

      {step === 0 && (
        <div className="grid-2">
          <form onSubmit={startRun} className="card form-card grid-form">
            <h3 className="full">New payroll run</h3>
            <label>Month
              <select name="month" defaultValue={new Date().getMonth() + 1} required>
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </label>
            <label>Year
              <input name="year" type="number" defaultValue={new Date().getFullYear()} required />
            </label>
            <label className="full">Notes<input name="notes" /></label>
            <div className="full actions">
              <button type="submit" className="btn primary" disabled={loading}>{loading ? 'Working…' : 'Configure & Preview →'}</button>
            </div>
          </form>

          <div className="card">
            <h3>Existing runs</h3>
            <table className="data-table">
              <thead><tr><th>Period</th><th>Status</th><th>Processed</th><th></th></tr></thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.id}>
                    <td>{r.period_year}-{String(r.period_month).padStart(2, '0')}</td>
                    <td><span className={`chip status-${r.status}`}>{r.status}</span></td>
                    <td className="muted">{r.processed_at?.slice(0, 10) || '—'}</td>
                    <td><button className="btn ghost small" onClick={() => loadExisting(r)}>Open</button></td>
                  </tr>
                ))}
                {runs.length === 0 && <tr><td colSpan={4} className="muted">No runs yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {step === 1 && preview && (
        <div className="card">
          <h3>Preview · {run?.period_year}-{String(run?.period_month).padStart(2, '0')}</h3>
          <p className="muted">{preview.items.length} employees · gross {preview.total.gross.toFixed(2)} · tax {preview.total.tax.toFixed(2)} · net {preview.total.net.toFixed(2)}</p>
          <table className="data-table">
            <thead><tr><th>Employee</th><th>Code</th><th>Currency</th><th>Gross</th><th>Tax</th><th>Net</th></tr></thead>
            <tbody>
              {preview.items.map((it) => (
                <tr key={it.employeeId}>
                  <td>{it.employee?.name || '—'}</td>
                  <td>{it.employee?.employee_code || '—'}</td>
                  <td>{it.currency}</td>
                  <td>{it.gross.toFixed(2)}</td>
                  <td>{it.tax.toFixed(2)}</td>
                  <td><strong>{it.net.toFixed(2)}</strong></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="actions">
            <button className="btn ghost" onClick={() => setStep(0)}>Back</button>
            <button className="btn primary" onClick={() => setStep(2)}>Continue →</button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="card">
          <h3>Confirm</h3>
          <p>Process payroll for <strong>{run?.period_year}-{String(run?.period_month).padStart(2, '0')}</strong> covering {preview?.items?.length} employees.</p>
          <p className="muted">This will lock the run and generate per-employee payroll items.</p>
          <div className="actions">
            <button className="btn ghost" onClick={() => setStep(1)}>Back</button>
            <button className="btn primary" onClick={confirmRun} disabled={loading}>{loading ? 'Processing…' : 'Process Payroll'}</button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="card">
          <h3>Payslips</h3>
          <table className="data-table">
            <thead><tr><th>Employee</th><th>Gross</th><th>Tax</th><th>Net</th><th>Payslip</th><th></th></tr></thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id}>
                  <td>{it.employee_name} <span className="muted small">{it.employee_code}</span></td>
                  <td>{Number(it.gross).toFixed(2)}</td>
                  <td>{Number(it.tax).toFixed(2)}</td>
                  <td><strong>{Number(it.net).toFixed(2)}</strong></td>
                  <td>{it.payslip_url ? <a href={it.payslip_url} target="_blank" rel="noreferrer">PDF</a> : <span className="muted">—</span>}</td>
                  <td><button className="btn ghost small" onClick={() => generate(it.id)}>{it.payslip_url ? 'Regenerate' : 'Generate'}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
